import { useCallback } from 'react';
import { api } from '../lib/api';
import { formatAgo, formatCount, formatDate } from '../lib/format';
import { go } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { AccountLink, Loading, Pager, Search } from '../components/bits';
import { PageHead } from '../components/Shell';

interface Props {
  readonly token: string;
  readonly query: URLSearchParams;
}

export const Albums = ({ token, query }: Props) => {
  const q = query.get('q') ?? '';
  const offset = Number(query.get('offset')) || 0;
  const loaded = useLoad(() => api.listAlbums(token, { q, offset }), [token, q, offset]);
  const onSearch = useCallback((next: string) => go('/albums', { q: next }), []);
  const now = Date.now();

  return (
    <>
      <PageHead
        eyebrow="Albums"
        title="Every album"
        lede="A person's living set of pictures. Publishing one to a frame copies nothing; it records the intent."
        actions={<Search value={q} placeholder="Album name or owner email" onSearch={onSearch} />}
      />

      <Loading loaded={loaded}>
        {(page) => (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Album</th>
                    <th scope="col">Owner</th>
                    <th scope="col" className="num">
                      Pictures
                    </th>
                    <th scope="col" className="num">
                      On frames
                    </th>
                    <th scope="col">Created</th>
                    <th scope="col">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((album) => (
                    <tr key={album.id}>
                      <td>{album.label}</td>
                      <td>
                        <AccountLink account={album.owner} />
                      </td>
                      <td className="num">{formatCount(album.itemCount)}</td>
                      <td className="num">{formatCount(album.publishedCount)}</td>
                      <td>{formatDate(album.createdAt)}</td>
                      <td>{formatAgo(album.updatedAt, now)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} noun="albums" onOffset={(next) => go('/albums', { q, offset: next })} />
          </>
        )}
      </Loading>
    </>
  );
};
